import {
  Alert,
  Avatar,
  Box,
  Chip,
  Container,
  Fade,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutline";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import { useNavigate } from "react-router-dom";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";
import SurfaceCard from "../common/ui/SurfaceCard";
import PrimaryButton from "../common/ui/PrimaryButton";

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth();
const monthYearLabel = new Date(currentYear, currentMonth, 1).toLocaleString(undefined, {
  month: "long",
  year: "numeric",
});

const dateInCurrentMonth = (day: number) => new Date(currentYear, currentMonth, day);

const dummyExpenses = [
  { id: "1", description: "Coffee at Starbucks", amount: 4.5, date: dateInCurrentMonth(20) },
  { id: "2", description: "Groceries", amount: 45.0, date: dateInCurrentMonth(18) },
  { id: "3", description: "Movie ticket", amount: 12.0, date: dateInCurrentMonth(15) },
];

const dummyIncomes = [
  { id: "1", description: "Freelance Project", amount: 500, date: dateInCurrentMonth(21) },
  { id: "2", description: "Salary", amount: 3000, date: dateInCurrentMonth(1) },
  { id: "3", description: "Gift", amount: 100, date: dateInCurrentMonth(10) },
];

const expenseCategoriesPreview = [
  { name: "Food", amount: 186 },
  { name: "Transport", amount: 72 },
  { name: "Subscriptions", amount: 48 },
  { name: "Other", amount: 31.5 },
];

const guestFlow = [
  "Add your first income and expense in under a minute.",
  "Auto-group transactions by category and month.",
  "Review trends and act on insights before month-end.",
];

const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

const GuestHomePageComponent = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  const accentSoft = alpha(accent, 0.2);
  const accentSofter = alpha(accent, 0.12);

  const totalExpenses = dummyExpenses.reduce((sum, e) => sum + e.amount, 0);
  const totalIncome = dummyIncomes.reduce((sum, i) => sum + i.amount, 0);
  const netBalance = totalIncome - totalExpenses;
  const expenseCategoryTotal = expenseCategoriesPreview.reduce((sum, item) => sum + item.amount, 0);
  const savingsRate = totalIncome > 0 ? Math.round((netBalance / totalIncome) * 100) : 0;
  const previewActivity = [...dummyExpenses.map((expense) => ({
    id: `exp-${expense.id}`,
    description: expense.description,
    amount: expense.amount,
    date: expense.date,
    type: "expense" as const,
  })), ...dummyIncomes.map((income) => ({
    id: `inc-${income.id}`,
    description: income.description,
    amount: income.amount,
    date: income.date,
    type: "income" as const,
  }))]
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    .slice(0, 5);

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
      <Fade in={true} timeout={1000}>
        <Box>
          <SurfaceCard
            sx={{
              mb: 3,
              p: { xs: 2.5, md: 4 },
              borderRadius: "18px",
              border: `1px solid ${accentSoft}`,
              background: `linear-gradient(150deg, ${theme.palette.background.paper}, ${theme.palette.background.default})`,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: "-170px auto auto -90px",
                width: 320,
                height: 320,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${accentSoft} 0%, transparent 66%)`,
              },
            }}
          >
            <Grid container spacing={3} alignItems="center" sx={{ position: "relative", zIndex: 1 }}>
              <Grid item xs={12} md={7}>
                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1.25 }}>
                  <Avatar sx={{ bgcolor: accentSoft, color: theme.palette.primary.light }}>
                    <InsightsOutlinedIcon />
                  </Avatar>
                  <Chip
                    label={`${monthYearLabel} preview`}
                    sx={{
                      borderRadius: "10px",
                      backgroundColor: accentSofter,
                      color: theme.palette.primary.light,
                      fontWeight: 700,
                    }}
                  />
                </Stack>

                <Typography
                  variant="h3"
                  sx={{
                    mb: 1,
                    fontFamily: '"Space Grotesk", "Manrope", sans-serif',
                    fontWeight: 700,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Welcome to PocketBalance
                </Typography>

                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
                  Track income and expenses in one clean dashboard, spot trends early, and make better money decisions with confidence.
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ mt: 2.3 }}>
                  <PrimaryButton startIcon={<LoginOutlinedIcon />} onClick={() => navigate(LOGIN_ROUTE_PATH)}>
                    Sign In to Start Tracking
                  </PrimaryButton>
                  <Chip
                    icon={<PieChartOutlineIcon />}
                    label="Interactive reports and monthly insights"
                    variant="outlined"
                    sx={{ borderRadius: "10px", width: { xs: "100%", sm: "auto" } }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={5}>
                <Alert
                  severity="info"
                  icon={false}
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: accentSofter,
                    border: `1px solid ${accentSoft}`,
                    color: theme.palette.primary.light,
                    py: 0.8,
                  }}
                >
                  Preview values below show how your personal dashboard will look once your own transactions are added.
                </Alert>
              </Grid>
            </Grid>
          </SurfaceCard>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} lg={4}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Total Expenses
                  </Typography>
                  <MoneyOffIcon sx={{ color: theme.palette.error.main }} />
                </Stack>
                <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 700, color: theme.palette.error.light }}>
                  {formatCurrency(totalExpenses)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Monthly preview
                </Typography>
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} sm={6} lg={4}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Total Income
                  </Typography>
                  <AttachMoneyIcon sx={{ color: theme.palette.success.main }} />
                </Stack>
                <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 700, color: theme.palette.success.light }}>
                  {formatCurrency(totalIncome)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Monthly preview
                </Typography>
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} sm={12} lg={4}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Net Balance
                  </Typography>
                  <TrendingUpIcon sx={{ color: accent }} />
                </Stack>
                <Typography
                  variant="h5"
                  sx={{
                    mt: 0.8,
                    fontWeight: 700,
                    color: netBalance >= 0 ? theme.palette.success.light : theme.palette.error.light,
                  }}
                >
                  {netBalance >= 0 ? "+" : ""}
                  {formatCurrency(netBalance)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Income - Expenses
                </Typography>
              </SurfaceCard>
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            <Grid item xs={12} lg={6}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px", height: "100%" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  How It Works
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  A focused workflow designed for speed and clarity.
                </Typography>

                <Stack spacing={1.1}>
                  {guestFlow.map((step, index) => (
                    <Box
                      key={step}
                      sx={{
                        p: 1.15,
                        borderRadius: "10px",
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                        backgroundColor: alpha(theme.palette.primary.main, 0.06),
                      }}
                    >
                      <Stack direction="row" spacing={1.1} alignItems="flex-start">
                        <Avatar
                          sx={{
                            width: 22,
                            height: 22,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.primary.main, 0.3),
                            color: theme.palette.primary.light,
                          }}
                        >
                          {index + 1}
                        </Avatar>
                        <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                          {step}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                <Alert
                  severity={savingsRate >= 20 ? "success" : "info"}
                  icon={false}
                  sx={{ mt: 1.5, borderRadius: "10px" }}
                >
                  Monthly savings potential: <strong>{savingsRate}%</strong> based on current values.
                </Alert>
              </SurfaceCard>
            </Grid>

            <Grid item xs={12} lg={6}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px", height: "100%" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Expense Split Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Instantly see where money goes by category.
                </Typography>

                <Stack spacing={1.1}>
                  {expenseCategoriesPreview.map((category) => {
                    const ratio = expenseCategoryTotal ? (category.amount / expenseCategoryTotal) * 100 : 0;
                    return (
                      <Box key={category.name}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.45 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {category.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(category.amount)} ({Math.round(ratio)}%)
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            height: 8,
                            borderRadius: 999,
                            backgroundColor: alpha(theme.palette.text.primary, 0.14),
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${ratio}%`,
                              height: "100%",
                              borderRadius: 999,
                              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>

                <Chip
                  icon={<PieChartOutlineIcon />}
                  label="Category analytics updated in real time"
                  sx={{ mt: 1.6, borderRadius: "10px" }}
                />
              </SurfaceCard>
            </Grid>

            <Grid item xs={12}>
              <SurfaceCard sx={{ p: 2.2, borderRadius: "16px" }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Activity Timeline Preview
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.4 }}>
                  One feed for all money movement.
                </Typography>

                <Stack spacing={1}>
                  {previewActivity.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        p: 1.1,
                        borderRadius: "10px",
                        border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                        backgroundColor: alpha(theme.palette.background.paper, 0.45),
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={1.2} alignItems="center">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                            {item.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label={`${item.type === "income" ? "+" : "-"}${formatCurrency(item.amount)}`}
                          sx={{
                            color: item.type === "income" ? theme.palette.success.light : theme.palette.error.light,
                            borderColor: item.type === "income"
                              ? alpha(theme.palette.success.main, 0.4)
                              : alpha(theme.palette.error.main, 0.45),
                          }}
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </SurfaceCard>
            </Grid>
          </Grid>

          <SurfaceCard sx={{ mt: 3, p: { xs: 2.2, sm: 2.8 }, textAlign: "center", borderRadius: "16px" }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.6 }}>
              Ready to use your own numbers?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.6 }}>
              Sign in and your dashboard becomes fully personalized with live transactions, categories, and reports.
            </Typography>
            <PrimaryButton startIcon={<LoginOutlinedIcon />} onClick={() => navigate(LOGIN_ROUTE_PATH)}>
              Sign In
            </PrimaryButton>
          </SurfaceCard>
        </Box>
      </Fade>
    </Container>
  );
};

export default GuestHomePageComponent;
